import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import type { PipelineStep } from '../../types'

interface PipelineProgressProps {
  steps: PipelineStep[]
  jobLabel: string
  operatorNotes?: string
}

/* ── Flavor phrases for the bottom carousel ────────────────────────── */

const FLAVOR_PHRASES = [
  'Reading drawing entities...',
  'Calculating feed rates...',
  'Optimizing cut sequences...',
  'Selecting cutter compensation...',
  'Computing stepover distances...',
  'Verifying safe Z clearance...',
  'Mapping tool change positions...',
  'Interpolating arc segments...',
  'Building rapid traverse paths...',
  'Checking spindle speed limits...',
]

/* ── Animated check circle SVG ─────────────────────────────────────── */

function CheckCircle({ status }: { status: PipelineStep['status'] }) {
  if (status === 'done') {
    return (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" className="shrink-0">
        <circle
          cx="9" cy="9" r="7.5"
          stroke="rgb(52,211,153)" strokeWidth="2"
          fill="rgba(52,211,153,0.15)"
        />
        <motion.path
          d="M5.5 9.5L7.8 11.8L12.5 6.5"
          stroke="rgb(52,211,153)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        />
      </svg>
    )
  }

  if (status === 'running') {
    return (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" className="shrink-0">
        {/* Subtle glow ring */}
        <circle
          cx="9" cy="9" r="7.5"
          stroke="rgba(6,182,212,0.2)" strokeWidth="3"
          fill="rgba(34,211,238,0.08)"
        />
        {/* Rotating dash arc — transform-origin set to center of circle */}
        <circle
          cx="9" cy="9" r="7.5"
          stroke="rgb(34,211,238)" strokeWidth="2"
          fill="none"
          strokeDasharray="12 35"
          strokeLinecap="round"
          style={{ transformOrigin: '9px 9px' }}
          className="animate-[spin_1.2s_linear_infinite]"
        />
      </svg>
    )
  }

  if (status === 'error') {
    return (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" className="shrink-0">
        <circle
          cx="9" cy="9" r="7.5"
          stroke="rgb(248,113,113)" strokeWidth="2"
          fill="rgba(248,113,113,0.15)"
        />
        <path
          d="M6.5 6.5L11.5 11.5M11.5 6.5L6.5 11.5"
          stroke="rgb(248,113,113)" strokeWidth="2" strokeLinecap="round"
        />
      </svg>
    )
  }

  // pending
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" className="shrink-0">
      <circle
        cx="9" cy="9" r="7.5"
        stroke="rgb(51,65,85)" strokeWidth="1.5"
        fill="transparent"
      />
    </svg>
  )
}

/* ── Step row (with children support) ──────────────────────────────── */

function StepRow({ step, isChild = false }: { step: PipelineStep; isChild?: boolean }) {
  const labelColor = {
    done: 'text-slate-500',
    running: 'text-cyan-300',
    error: 'text-red-400',
    pending: 'text-slate-600',
  }[step.status]

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
    >
      <div className={`flex items-center gap-2.5 py-1.5 ${isChild ? 'pl-7' : ''}`}>
        <CheckCircle status={step.status} />
        <span className={`font-mono text-sm tracking-wide ${labelColor} transition-colors duration-300`}>
          {step.label}
        </span>
        {step.detail && (
          <span className="ml-auto font-mono text-xs text-slate-600 tabular-nums">
            {step.detail}
          </span>
        )}
      </div>

      {step.children && step.children.length > 0 && (
        <AnimatePresence>
          {step.children.map((child) => (
            <StepRow key={child.id} step={child} isChild />
          ))}
        </AnimatePresence>
      )}
    </motion.div>
  )
}

/* ── Phrase carousel ───────────────────────────────────────────────── */

const LINE_HEIGHT = 20

function PhraseCarousel() {
  const [index, setIndex] = useState(0)

  useEffect(() => {
    const id = setInterval(() => {
      setIndex((prev) => (prev + 1) % FLAVOR_PHRASES.length)
    }, 3000)
    return () => clearInterval(id)
  }, [])

  return (
    <div
      className="relative overflow-hidden mx-auto text-center"
      style={{ height: LINE_HEIGHT }}
    >
      <motion.div
        animate={{ y: -index * LINE_HEIGHT }}
        transition={{ duration: 0.5, ease: 'easeInOut' }}
      >
        {FLAVOR_PHRASES.map((phrase) => (
          <div
            key={phrase}
            className="font-mono text-xs text-slate-500"
            style={{ height: LINE_HEIGHT, lineHeight: `${LINE_HEIGHT}px` }}
          >
            {phrase}
          </div>
        ))}
      </motion.div>
    </div>
  )
}

/* ── Main component ────────────────────────────────────────────────── */

export default function PipelineProgress({ steps, jobLabel, operatorNotes }: PipelineProgressProps) {
  const doneCount = steps.filter((s) => s.status === 'done').length
  const errorCount = steps.filter((s) => s.status === 'error').length
  const totalCount = steps.length
  const progress = totalCount > 0 ? doneCount / totalCount : 0
  const hasError = errorCount > 0
  const isRunning = steps.some((s) => s.status === 'running') || steps.length === 0

  return (
    <div className="w-full">
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className={`rounded-2xl border backdrop-blur-sm px-8 py-8 ${
          hasError ? 'border-red-500/20' : 'border-cyan-500/10'
        }`}
        style={{
          background:
            'linear-gradient(135deg, rgba(6,182,212,0.03) 0%, rgba(13,21,38,0.95) 50%, rgba(3,7,18,0.85) 100%)',
          boxShadow:
            '0 0 40px rgba(6,182,212,0.04), 0 8px 32px rgba(0,0,0,0.5)',
        }}
      >
        {/* Header */}
        <div className="text-center mb-6">
          <h2 className={`font-mono text-base tracking-wide bg-clip-text text-transparent ${
            hasError
              ? 'bg-gradient-to-r from-red-400 to-red-300'
              : 'bg-gradient-to-r from-cyan-400 to-cyan-300'
          }`}>
            {hasError ? 'Generation failed' : 'Generating G-code'}
          </h2>
          <p className="font-mono text-xs text-slate-600 mt-1.5 tracking-wider">
            {jobLabel}
          </p>
        </div>

        {/* Step list */}
        <div className="mb-5 space-y-0.5">
          <AnimatePresence>
            {steps.map((step) => (
              <StepRow key={step.id} step={step} />
            ))}
          </AnimatePresence>

          {steps.length === 0 && (
            <div className="flex items-center justify-center gap-2.5 py-4">
              <CheckCircle status="running" />
              <span className="font-mono text-sm text-slate-500">
                Initializing pipeline...
              </span>
            </div>
          )}
        </div>

        {/* Operator notes — appear after geometry extraction */}
        <AnimatePresence>
          {operatorNotes && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="mb-5 rounded-lg border border-cyan-500/10 bg-white/[0.02] px-4 py-3"
            >
              <p className="font-mono text-[10px] text-slate-500 tracking-widest uppercase mb-1.5">
                Extracted Part Summary
              </p>
              <pre className="font-mono text-xs text-slate-400 leading-relaxed whitespace-pre-wrap">
                {operatorNotes}
              </pre>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Progress bar */}
        <div className="h-0.5 w-full bg-slate-800 rounded-full mb-5 overflow-hidden">
          <motion.div
            className={`h-full rounded-full ${
              hasError
                ? 'bg-gradient-to-r from-red-500/60 to-red-400/80'
                : 'bg-gradient-to-r from-cyan-500/60 to-cyan-400/80'
            }`}
            initial={{ width: 0 }}
            animate={{ width: `${progress * 100}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </div>

        {/* Phrase carousel or error message */}
        {hasError ? (
          <p className="font-mono text-xs text-red-400/70 text-center">
            One or more operations failed. Check chat for details.
          </p>
        ) : isRunning ? (
          <PhraseCarousel />
        ) : null}
      </motion.div>
    </div>
  )
}
